import React, { useState } from 'react';
import { X, Headphones, AlertCircle, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { assistanceService, AssistanceRequestCreate } from '../../../services/AssistanceService';

interface AssistanceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number;
  sessionId: string;
  orderName?: string;
  onSuccess?: () => void;
}

const AssistanceRequestModal: React.FC<AssistanceRequestModalProps> = ({
  isOpen,
  onClose,
  orderId,
  sessionId,
  orderName,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal');
  const [contactPreference, setContactPreference] = useState<'email' | 'phone' | 'chat'>('chat');
  const [contactInfo, setContactInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !description.trim()) {
      setError(t('assistance.errors.requiredFields'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const requestData: AssistanceRequestCreate = {
        order_id: orderId,
        session_id: sessionId,
        subject: subject.trim(),
        description: description.trim(),
        priority,
        contact_preference: contactPreference,
        contact_info: contactInfo.trim() || undefined
      };

      await assistanceService.createAssistanceRequest(requestData);
      setSuccess(true);

      // Call onSuccess callback after a short delay
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error submitting assistance request:', err);
      setError(t('assistance.errors.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setSubject('');
    setDescription('');
    setPriority('normal');
    setContactPreference('chat');
    setContactInfo('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  const priorityOptions = [
    { value: 'low', label: t('assistance.priority.low'), color: 'bg-gray-100 text-gray-700 border-gray-300' },
    { value: 'normal', label: t('assistance.priority.normal'), color: 'bg-blue-100 text-blue-700 border-blue-300' },
    { value: 'high', label: t('assistance.priority.high'), color: 'bg-orange-100 text-orange-700 border-orange-300' },
    { value: 'urgent', label: t('assistance.priority.urgent'), color: 'bg-red-100 text-red-700 border-red-300' }
  ];

  const contactOptions = [
    { value: 'chat', label: t('assistance.contact.chat') },
    { value: 'email', label: t('assistance.contact.email') },
    { value: 'phone', label: t('assistance.contact.phone') }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-orange-600 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {t('assistance.title')}
              </h2>
              {orderName && (
                <p className="text-sm text-orange-100">
                  {orderName}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success Message */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('assistance.success.title')}
            </h3>
            <p className="text-gray-600">
              {t('assistance.success.message')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('assistance.form.subject')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('assistance.form.subjectPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                maxLength={200}
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('assistance.form.description')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('assistance.form.descriptionPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                rows={4}
                required
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('assistance.form.priority')}
              </label>
              <div className="flex flex-wrap gap-2">
                {priorityOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setPriority(option.value as typeof priority)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-all ${priority === option.value
                        ? option.color + ' ring-2 ring-offset-1 ring-current'
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Contact Info */}
            {contactPreference !== 'chat' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {contactPreference === 'email'
                    ? t('assistance.form.emailAddress')
                    : t('assistance.form.phoneNumber')
                  }
                </label>
                <input
                  type={contactPreference === 'email' ? 'email' : 'tel'}
                  value={contactInfo}
                  onChange={(e) => setContactInfo(e.target.value)}
                  placeholder={contactPreference === 'email'
                    ? t('assistance.form.emailPlaceholder')
                    : t('assistance.form.phonePlaceholder')
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !subject.trim() || !description.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('assistance.form.submitting')}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t('assistance.form.submit')}
                  </>
                )}
              </button>
            </div>

            {/* Help text */}
            <p className="text-xs text-gray-500 text-center">
              {t('assistance.form.helpText')}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default AssistanceRequestModal;
